import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { useSession } from "@/context/Session";
import { AnswerCard } from "@/components/AnswerCard";
import { MessageList, buildGroups, groupKey, isUnread } from "@/components/MessageList";
import { Composer } from "@/components/Composer";
import { ClearChat } from "@/components/ClearChat";
import { reviewChanged } from "@/components/AppShell";
import { DrinkTemplate } from "@/components/DrinkTemplate";
import { PendingBubble } from "@/components/PendingBubble";
import { HeroTip } from "@/components/HeroTip";
import { SourceImage } from "@/components/SourceImage";
import { SpecialPrompt, specialDue } from "@/components/SpecialPrompt";
import { RotateCcw } from "lucide-react";
import { presetFor } from "@/lib/presets";
import { OiCounter } from "@/components/OiCounter";
import { CouponAlerts } from "@/components/CouponAlerts";

const WELCOME_QS = ["How long do I fry Kelp Fries?", "What are the opening steps?", "Can I give a customer a $10 refund?"];
import { SpecialsBoard } from "@/components/SpecialsBoard";
import type { Resubmit } from "@/components/DrinkTemplate";
import { StatusBadge } from "@/components/Badges";
import { pick, useOriginal } from "@/components/Translate";
import { t } from "@/i18n";
import { Tt } from "@/components/Tt";


const CaptureBubble = ({ s, onFix }: { s: any; onFix: (s: any) => void }) => (
  <div className="flex flex-col items-end" data-testid={`capture-bubble-${s.id}`}>
    <Link to={`/sops/${s.id}`} className="bubble-out max-w-[80%] hover:opacity-90 transition-opacity !p-1.5">
      {s.status === "published" && s.cover_source_id && <SourceImage sourceId={s.cover_source_id} className="w-64 max-w-full h-40 rounded-[1rem]" testid={`capture-cover-${s.id}`} />}
      <span className="block px-2.5 pt-1 pb-1">
        <span className="block text-xs opacity-80">{t("Recorded OI")}</span>
        <span className="font-semibold"><Tt text={s.title} /></span>
      </span>
    </Link>
    <span className="mt-1 px-2"><StatusBadge status={s.status} /></span>
    {s.status === "rejected" && s.reject_reason && <p className="text-xs muted mt-1 px-2 max-w-[80%] text-right" data-testid={`capture-reject-reason-${s.id}`}>{t("Owner")}: “{s.reject_reason}”</p>}
    {s.status === "rejected" && (s.resubmitted_as
      ? <span className="text-xs faint mt-1 px-2" data-testid={`capture-resubmitted-${s.id}`}>{t("Resubmitted")}</span>
      : <button className="btn btn-sm btn-ghost mt-1 !text-[var(--accent-primary)]" onClick={() => onFix(s)} data-testid={`capture-fix-button-${s.id}`}><RotateCcw size={13} /> {t("Fix & resubmit")}</button>)}
    {s.prev_reject_reason && s.status !== "rejected" && <p className="text-xs faint mt-1 px-2 max-w-[80%] text-right" data-testid={`capture-prev-note-${s.id}`}>{t("Fixed after")}: “{s.prev_reject_reason}”</p>}
  </div>
);

export default function Chat() {
  const api = useApi();
  const { me } = useSession();
  const [thread, setThread] = useState<any[]>([]);
  const [subs, setSubs] = useState<any[]>([]);
  const [selected, setSelected] = useState("all");
  const [busy, setBusy] = useState(false);
  const [promoted, setPromoted] = useState<string[]>([]);
  const [pending, setPending] = useState<any[]>([]);
  const [drinkOpen, setDrinkOpen] = useState(false);
  const [drinkName, setDrinkName] = useState("");
  const preset = presetFor(me.org?.preset);
  const welcome = new URLSearchParams(window.location.search).get("welcome") === "1";
  const landlord = me.org?.type === "property_management";
  const [tplKey, setTplKey] = useState(preset.templates[0].key);
  const tpl = preset.templates.find((x) => x.key === tplKey) || preset.templates[0];
  const [resub, setResub] = useState<Resubmit | null>(null);
  const orig = useOriginal();
  const [showSpecial, setShowSpecial] = useState(() => me.is_maintainer && specialDue());
  const end = useRef<HTMLDivElement>(null);
  const recordLabel = t(me.is_maintainer ? "Save to OI" : "Submit for approval");

  useEffect(() => {
    api.get("/questions/mine").then((r) => setThread(r.data));
    api.get("/sops/mine").then((r) => setSubs(r.data.map((s: any) => (s.rejected ? { ...s, status: "rejected" } : s))));
  }, [api]);
  useEffect(() => {
    if (!me.is_maintainer) return;
    const load = () => api.get("/sops/pending").then((r) => setPending((p) => {
      const fresh = r.data.map((x: any) => x.id);
      return [...p.filter((x) => x.decision && !fresh.includes(x.id)), ...r.data];
    })).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [api, me.is_maintainer]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length, subs.length, busy, selected]);

  const ask = async (question: string) => {
    setBusy(true);
    setThread((t) => [...t, { pending: true, id: "pending", question, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post("/ask", { question });
      setThread((t) => [...t.filter((x) => !x.pending), data]);
      if (data.routed) reviewChanged();
    } catch (e) {
      toast.error(errMsg(e));
      setThread((t) => t.filter((x) => !x.pending));
    }
    setBusy(false);
  };

  const record = async (files: File[], note: string, title = "", extra: Record<string, string> = {}) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("note", note);
    fd.append("title_hint", title);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      const { data } = await api.post("/captures", fd);
      const prev = extra.resubmit_of ? subs.find((x) => x.id === extra.resubmit_of) : null;
      setSubs((s) => [{ id: data.id, title: title || note.slice(0, 60) || t("New recording"), status: "processing", created_at: new Date().toISOString(), prev_reject_reason: prev?.reject_reason },
        ...s.map((x) => (x.id === extra.resubmit_of ? { ...x, resubmitted_as: data.id } : x))]);
      toast.success(t(me.is_maintainer ? "Saved — drafting the SOP" : prev ? "Resubmitted to the owner" : "Sent to the owner for approval"));
      setBusy(false);
      return true;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); return false; }
  };

  const promote = async (q: any) => {
    if (promoted.includes(q.id)) return;
    setPromoted((p) => [...p, q.id]);
    const fd = new FormData();
    fd.append("note", `Question: ${q.question}\nOwner answer: ${q.owner_reply}`);
    try {
      const { data } = await api.post("/captures", fd);
      setSubs((s) => [{ id: data.id, title: q.question.slice(0, 60), status: "processing", created_at: new Date().toISOString() }, ...s]);
      toast.success(t(me.is_maintainer ? "Saving to OI" : "Submitted for approval"));
      reviewChanged();
    } catch (e) { toast.error(errMsg(e)); setPromoted((p) => p.filter((x) => x !== q.id)); }
  };

  const select = (key: string) => {
    setSelected(key);
    const ids = thread.filter((q) => isUnread(q) && (key === "all" || groupKey(q) === key)).map((q) => q.id);
    if (!ids.length) return;
    api.post("/questions/seen", { ids }).catch(() => {});
    setThread((t) => t.map((q) => (ids.includes(q.id) ? { ...q, reply_seen: true } : q)));
  };
  const react = async (id: string, reaction: string | null) => {
    try {
      await api.post(`/questions/${id}/react`, { reaction });
      setThread((t) => t.map((q) => (q.id === id ? { ...q, reaction } : q)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const escalate = async (id: string) => {
    try {
      await api.post(`/questions/${id}/escalate`);
      setThread((t) => t.map((q) => (q.id === id ? { ...q, routed: true, status: "open" } : q)));
      toast.success(t("Sent to the owner"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const clearChat = async () => {
    try {
      await api.post("/chat/clear");
      setThread([]); setSubs([]); setSelected("all");
      toast.success(t("Chat cleared"));
    } catch (e) { toast.error(errMsg(e)); }
  };


  const items = selected === "all"
    ? [...thread, ...subs.map((s) => ({ ...s, kind: "capture" })), ...pending.map((s) => ({ ...s, kind: "pending" }))]
        .sort((a, b) => (a.created_at > b.created_at ? 1 : -1))
    : thread.filter((q) => q.pending || groupKey(q) === selected);
  const tip = (kind: string) => {
    if (kind === "ask") ask(t(preset.askExample));
    else if (kind !== "record") openDrink("", kind);
    else toast(t("Tap the mic below to start recording"));
  };
  const settle = (id: string, status: string, reason = "") => setPending((p) => p.map((x) => (x.id === id
    ? { ...x, status, decision: status === "published" ? "approved" : "rejected", decided_at: new Date().toISOString(), decided_by: me.name, reject_reason: reason }
    : x)));
  const openDrink = (name = "", key = "") => {
    setResub(null); setDrinkName(name); setTplKey(preset.templates.find((x) => x.key === key)?.key || preset.templates[0].key); setDrinkOpen(true);
  };
  const fix = async (s: any) => {
    try {
      const { data } = await api.get(`/sops/${s.id}`);
      const txt: string = (data.sources.find((x: any) => x.kind === "text")?.extracted_text || "").replace(/\r\n?/g, "\n");
      const m = txt.match(/^([^:\n]{1,40}):\s*(.*)\n\n([\s\S]*)$/);
      const used = m ? preset.templates.find((x) => x.prefix === m[1]) : undefined;
      setTplKey((used || preset.templates[0]).key);
      const details = (m && used ? m[3] : txt).replace(/^Ingredients and amounts:\n/, "").replace(/\n\nSteps:\n/, "\n\n")
        .replace(/\(see attachments\)/g, "").trim();
      setResub({ id: s.id, reason: s.reject_reason || "", name: m && used ? m[2].trim() : s.title, details,
        kept: data.sources.filter((x: any) => x.kind !== "text").length });
      setDrinkOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-12 gap-6">
      <aside className="hidden md:block md:col-span-4 lg:col-span-3 py-6 stack sticky top-14 self-start max-h-[calc(100vh-3.5rem)] overflow-y-auto">
        <MessageList groups={buildGroups(thread)} selected={selected} onSelect={select} total={thread.filter((q) => !q.pending).length} />
        <OiCounter refresh={thread.length} />
        <CouponAlerts />
      </aside>
      <section className="min-w-0 md:col-span-8 lg:col-span-9 flex flex-col min-h-[calc(100vh-3.5rem-4.5rem)] md:min-h-[calc(100vh-3.5rem)]">
        <div className="flex-1 flex flex-col gap-4 py-6" data-testid="ask-thread">
          {welcome && <div className="card stack-sm rise" data-testid="welcome-card">
            <p className="font-semibold">{t("Welcome to {org}, {name}!", { org: me.org?.name, name: me.nickname })}</p>
            <p className="text-sm muted">{t("Ask anything about the job. Try one of these:")}</p>
            <div className="flex flex-wrap gap-1.5">{WELCOME_QS.map((s, i) => <button key={s} className="chip chip-muted !py-1.5 hover:text-[var(--accent-primary)]" onClick={() => ask(t(s))} disabled={busy} data-testid={`welcome-question-${i}`}>{t(s)}</button>)}</div>
          </div>}
          {items.length === 0 && !showSpecial && !welcome && <HeroTip tips={preset.tips} onAction={tip} />}
          {items.map((q) => q.kind === "capture" ? <CaptureBubble key={q.id} s={q} onFix={fix} /> : q.kind === "pending" ? <PendingBubble key={q.id} s={q} api={api} onDone={settle} /> : (
            <div key={q.id} className="flex flex-col gap-2">
              <div className="flex justify-end"><p className="max-w-[80%] bubble-out text-[0.97rem]" data-testid="ask-user-bubble">{pick(q, "question", orig.isOriginal(q.id))}</p></div>
              {q.pending ? <p className="bubble-in w-16 text-center" data-testid="ask-pending"><span className="pulse-dot">•••</span></p>
                : <AnswerCard q={q} onEscalate={escalate} onReact={react} onPromote={promote} promoted={promoted.includes(q.id)} promoteLabel={recordLabel} original={orig.isOriginal(q.id)} onToggleOriginal={orig.toggle} />}
            </div>
          ))}
          {showSpecial && !landlord && selected === "all" && <SpecialPrompt onRecord={() => { setShowSpecial(false); openDrink(t("Today's special")); }} onDismiss={() => setShowSpecial(false)} />}
          <div ref={end} />
        </div>
        <div className="sticky bottom-[4.5rem] md:bottom-0 pb-4 pt-3 bg-[var(--bg-primary)] stack-sm">
          {!landlord && <SpecialsBoard />}
          <div className="flex flex-wrap gap-1.5 px-2">
            {preset.templates.map((x) => <button key={x.key} className="chip chip-ok !py-1.5" onClick={() => openDrink("", x.key)} data-testid={`template-${x.key}-button`}><x.icon size={13} /> {t(x.label)}</button>)}
            {thread.length === 0 && preset.suggest.map((s, i) => <button key={s} className="chip chip-muted !py-1.5 hover:text-[var(--accent-primary)]" onClick={() => ask(t(s))} disabled={busy} data-testid={`ask-suggest-${i}`}>{t(s)}</button>)}
            <ClearChat onClear={clearChat} disabled={!thread.length && !subs.length} />
          </div>
          <Composer busy={busy} recordLabel={recordLabel} onAsk={ask} onRecord={record} />
          <DrinkTemplate tpl={tpl} open={drinkOpen} onOpenChange={(o: boolean) => { setDrinkOpen(o); if (!o) setResub(null); }} label={recordLabel} busy={busy} onSubmit={record} initialName={drinkName} resubmit={resub} />
        </div>
      </section>
    </div>
  );
}
